/* Core & libraries */
import React, {useEffect, useState} from "react";
import { useTheme } from "react-jss";
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { usePaymentService } from '../../../services/paymentService.js';
import { useAnalytics } from "../../../hooks/useAnalytics.js";
import { PAGE_TITLES } from "../../../constants/appConstants.js";
import AssignModal from "../../modals/assignModal/index.js";
import ConfirmAssignModal from "../../modals/confirmAssignModal/index.js";
import { ReactComponent as CardIcon} from "../../../resources/images/credit-card-03.svg"
import { assignTypes } from "../../../constants/appConstants.js";
import { cardText } from "../../../helpers/general.js";
import { useGlobalContext } from "../../../contexts/globalContext.js";
import { useGlobalUpdateContext } from "../../../contexts/globalContext.js";

/* Components */
import PaymentsGrid from "./paymentsGrid/index.js";

/* Styles */
import styles from "./styles.js";


const PaymentTab = ({ addPayment, editPayment, viewPayment, paymentResponseData, providerCode }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const location = useLocation();
  const navigate = useNavigate();
  const { initAnalytics } = useAnalytics();

  const { paymentResponse, setPaymentResponse, getPaymentList, getAssignTeamsData, deletePayment, assignPaymentCard } = usePaymentService();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfirmAssignModal, setShowConfirmAssignModal] = useState(false);
  const [assignData, setAssignData] = useState({});
  const globalProperties  = useGlobalContext();
  const setGlobalProperties = useGlobalUpdateContext();
  const [initialGroupData, setInitialGroupData] = useState({});

  const [gridOptions, setGridOptions] = useState(() => {
    const initialPage = location.pathname.includes('/payment')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    return {
      page: +initialPage
    };
  });

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["payment"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, [])

  useEffect(() => {
    getPaymentList(gridOptions);
    gridOptions.page > 1 ? navigate(`/app/payment?page=${gridOptions.page}`) : navigate(`/app/payment`)
  }, [gridOptions]);

  useEffect(() => {
    setPaymentResponse(paymentResponseData)
  }, [paymentResponseData]);

  useEffect(() => {
    const pageFromURL = location.pathname.includes('/payment')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    if (+pageFromURL !== gridOptions.page) {
      setGridOptions({ ...gridOptions, page: pageFromURL });
    }
  }, [location.search]);

  //Check the global variable for a card that was added first time and open the assign modal
  useEffect(() => {
    const matchingFopUUID = paymentResponse?.results?.dataItems?.find(card => 
      card.cardType === globalProperties?.addFirstCardData?.cardType &&
      card.cardNumberMasked === globalProperties?.addFirstCardData?.cardNumberMasked &&
      card.nickname === globalProperties?.addFirstCardData?.nickname &&
      card.expirationDate === globalProperties?.addFirstCardData?.expirationDate
    )?.fopUUID
    
    if (globalProperties?.addFirstCardData?.firstTimeCardAdded) {
      displayShowAssignModal({
        ...globalProperties.addFirstCardData,
        fopUUID: matchingFopUUID
      })
    }
  }, [paymentResponse?.results?.dataItems?.length])

  //If the modal closes, set the "firstTimeCardAdded" to false so that the assign modal does not wrongfully opens again
  useEffect(() => {
    if(!showAssignModal) {
      setGlobalProperties({
        ...globalProperties,
        addFirstCardData: {
          ...globalProperties.addFirstCardData,
          firstTimeCardAdded: false
        }
      })
    }
  }, [showAssignModal])

  const handleDeletePayment = async (selectedRow) => {
    const isLastPaymentCardOnPage = paymentResponse?.results?.dataItems?.length === 1;

    const response = await deletePayment(selectedRow);

    if (!response) return false;
    
    const nextPage =
      isLastPaymentCardOnPage && gridOptions.page > 1
        ? gridOptions.page - 1
        : gridOptions.page;

    setGridOptions(prev => ({
      ...prev,
      page: nextPage
    }));

    return true;
  };

  const displayShowAssignModal = async (row) => {
    const response = await getAssignTeamsData(`assignTeamsButton${row.fopUUID}`, row.fopUUID);
    if(response) {
      setAssignData({
        response: response,
        row: row
      })
      setInitialGroupData(JSON.parse(JSON.stringify(response.groups)));
      setShowAssignModal(true)
    }
  }

  const handleAssign = () => {
    setShowAssignModal(false)
    setShowConfirmAssignModal(true)
  }

  const handleConfirmAssign = async () => {
    const idsArray = assignData.response.groups
      .filter(group => group.fopInfoList.some(fop => fop.fSelected))
      .map(group => group.entityID);

    const groupData = {
      cardInfo: assignData.row,
      vendorName: cardText(assignData.row.cardType),
      requestBody: {
        ids: idsArray,
        fopUUIDRefId: assignData.row.fopUUID
      }
    }
    const response = await assignPaymentCard(groupData) 

    if (response) {
      getPaymentList(gridOptions);
      setShowConfirmAssignModal(false);
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmAssignModal(false)
    setShowAssignModal(true)
  }
  const noTeamsAssigned = !assignData?.response?.groups.filter(group => group.fopInfoList.filter(card => card.fopUUID === assignData.row.fopUUID && card.fSelected === true).length > 0).length > 0

  return (
    <div className={classes.paymentPage}>
      <PaymentsGrid 
        paymentResponse={paymentResponse}
        gridOptions={gridOptions}
        setGridOptions={setGridOptions}
        displayShowAssignModal={displayShowAssignModal}
        deletePayment={handleDeletePayment}
        addPayment={addPayment}
        editPayment={editPayment}
        viewPayment={viewPayment}
      />
       <AssignModal 
        showModal={showAssignModal}
        setShowModal={setShowAssignModal}
        type={assignTypes.card}
        handleAssign={handleAssign}
        modalIcon={<CardIcon />}
        modalTitle={ <FormattedMessage id="assignModal.title" 
          values={{cardName: cardText(assignData?.row?.cardType), cardEndingNumber: assignData?.row?.cardNumberMasked}}/> 
        }
        cardText={cardText}
        modalDescription={ <FormattedMessage id={`assignModal.description.${providerCode}`} /> }
        assignData={assignData}
        setAssignData={setAssignData}
        confirmButtonText={<FormattedMessage id="assignModal.btnText" />}
        initialGroupData={initialGroupData}
      />
      <ConfirmAssignModal 
        showModal={showConfirmAssignModal}
        setShowModal={setShowConfirmAssignModal}
        handleConfirm={handleConfirmAssign}
        handleCancelConfirm={handleCancelConfirm}
        modalTitle={ noTeamsAssigned ? <FormattedMessage id="confirmAssignModal.noTeams.title" /> : <FormattedMessage id="confirmAssignModal.title" /> }
        modalDescription={ noTeamsAssigned ? <FormattedMessage id="confirmAssignModal.noTeams.description" /> : <FormattedMessage id="confirmAssignModal.description" /> }
        modalContent= { <FormattedMessage id="confirmAssignModal.content" />}
        modalIcon={<CardIcon />}
      />
    </div>
  );
};

export default PaymentTab;
